import { DropdownDataType } from "@/shared/ui/dropdown/types";
import { IUser } from "@/entities/users/types";
import { MarginalityCategory } from "@/entities/marginality/types";

export interface IDealer {
  id: string;
  name: string;
  shortName: string | null;
  inn: string | null;
  kpp: string | null;
  address: string | null;
  deliveryAddress: string | null;
  isMain: boolean;
  status: DealerRecordStatusType | null;
  contactFIO: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  users: IUser[];
  statusName: string;
  recordStatus?: DealerRecordStatusType | null;
  isExporter: boolean;
  isSelfWork: boolean;
  isSeparateLegalEntity?: boolean;
  taxNumber: string | null;
  bic: string | null;
  mainINN: string | null;
  marginalityCategoryIds: MarginalityCategory["id"][];
  marginalityCategories: MarginalityCategory[];
  cityId: number | null;
  cityFullName: string | null;
  federalDistrictName: string | null;
  dealerStatus: DealerStatusType | null;
}
export type SelectOptionsType = {
  value: string;
  label: string;
};

export interface IOrderedDealer extends IDealer {
  dealerName: string;
  productionPeriodOrderSettingsDeadline: string;
  quantity: number;
}

export type ChangeOrderDateBodyType = {
  prolongateDate: string;
  dealerIds: string[];
};

export type ProlongationBodyType = {
  dealerIds: string[];
  dateDeactivate: string;
};

export type ProductionOrderBodyType = {
  dealerIds: string[];
};

export type DealerStatusType = "Новый" | "Действующий";
export interface IDealerWithFilials extends IDealer {
  filials: IDealer[];
}

export type DealerSliceState = {
  dealersList: IDealer[];
  dealersListForFilter: IDealer[];
  branchesList: IDealer[];
  currentDealerUsersForFilter: IUser[];
  currentDealerBranchesForFilter: IDealer[];
  currentDealer: IDealer | null;
  currentDealerBranch: IDealer | null;
  error: null | string | unknown;
  isLoading: boolean;
  isMiniLoading: boolean;
  currentDealerUsers: IUser[];
  currentMainDealer: IDealer | null;
  hideBranchesColumnsList: (keyof IDealer)[];
  excludeItems: (keyof IDealer)[];
  branchesSort: StateSortType;
  filials: Partial<IDealer>[];
  filialsSortRule: FilialsSortType;
  mainFilial: Partial<IDealer> | null;
  selectedFilials: DropdownDataType[];
  hasUpdates: boolean;
  onlyExporters: boolean;
  onlySelfWorkers: boolean;
  onlyMyDealers: boolean;
  searchQuery: string;
  cities: SelectOptionsType[];
  dealerRemainsReports: IDealerRemainsReport[];
  dealerRemainsLastManualReport: string | null;
  dealerSalesReports: IDealerSalesReport[];
  dealerSalesLastManualReport: string | null;
  orderedDealers: IOrderedDealer[];
  selectedDealerIds: string[];
  isDatePickerActive: boolean;
  dealerSort: DealerSortState;
  isHandcraftOrderLoading: boolean;
};

export interface IDealerReport {
  weekNumber: number;
  createdDate: string;
  shortReportUrl: string;
  detailedReportUrl: string;
  isManual: boolean;
}

export interface DealerResponse {
  items: IDealer[];
}

export interface DealerReportsResponse {
  reports: IDealerRemainsReport[] | IDealerSalesReport[];
}

export interface OrderedDealersResponse {
  items: IOrderedDealer[];
}

export interface CitiesResponse {
  items: SelectOptionsType[];
}

export interface IDealerSalesReport extends IDealerReport {}
export interface IDealerRemainsReport extends IDealerReport {}

export type DealerRecordStatusType = 1 | 2 | 3 | 4 | 5;

export enum DealerRecordStatusEnum {
  _1 = "Новый",
  _2 = "На проверке",
  _3 = "Согласован",
  _4 = "Отклонен",
  _5 = "Архив",
}

export interface AddDealerDataType extends IDealer {
  mainDealerId: string;
  email: string;
  phoneNumber: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
}

export enum DealerEnum {
  shortName = "Краткое наименование",
  filials = "Филиалы",
  inn = "ИНН",
  kpp = "КПП",
  taxNumber = "Налоговый номер",
  deliveryAddress = "Адрес доставки",
  name = "Название",
  isMain = "Основной грузополучатель",
  address = "Юридический адрес",
  bic = "БИК",
  contactFIO = "ФИО контактного лица",
  contactPhone = "Телефон",
  contactEmail = "Email",
  marginalityCategories = "Категория",
  federalDistrictName = "Федеральный округ",
  cityFullName = "Город",
}
export enum DealerBranchEnum {
  status = "Статус",
  isMain = "",
  shortName = "Краткое наименование",
  name = "Полное наименование",
  inn = "ИНН",
  kpp = "КПП",
  deliveryAddress = "Адрес доставки",
  address = "Юридический адрес",
  contactFIO = "ФИО контактного лица",
  contactPhone = "Телефон",
  contactEmail = "Email",
}

export type DealerEditBodyType = Partial<
  Pick<
    IDealer,
    | "id"
    | "contactEmail"
    | "isMain"
    | "contactFIO"
    | "status"
    | "recordStatus"
    | "isExporter"
    | "isSelfWork"
    | "isSeparateLegalEntity"
    | "taxNumber"
    | "inn"
    | "kpp"
    | "marginalityCategoryIds"
    | "dealerStatus"
    | "cityFullName"
  > &
    Partial<AddDealerDataType>
>;

export type DealerBodyType = {
  filters?:
    | [
        {
          type: string;
          fields: {
            [fieldKey: keyof IDealer | string]:
              | (string | boolean | number)[]
              | boolean
              | string;
          };
        },
      ]
    | [];
  order?: {
    field: string;
    type: string;
  };
  pagination?: {
    pageSize: number;
    pageNumber: number;
  };
};

export type DealerSortState = {
  field: keyof IOrderedDealer | null;
  type: "asc" | "desc";
};

export type StateSortType = {
  type: "desc" | "asc";
  field: keyof typeof DealerBranchEnum;
};
export type FilialsSortType = {
  type: "desc" | "asc";
  field: keyof IDealer;
};

export type DealerSettingsType = "assort" | "price" | "subs";
export type DealerSecondarySettingsType = "name" | "code";
export type DealerPageSectionType = "users" | "settings" | "branches";

export enum SyncHandcraftOrdeStateEnum {
  Completed = "Completed",
  Started = "Started",
  Stopped = "Stoped",

  Failed = "Failed",

  Unknown = "Unknown",
}
